/**
 * AssignOrganizationsDialog Component
 *
 * Dialog for managing organization assignments to a Schema Group.
 * TerraMatrix admins use this to grant customer orgs access to groups.
 * Now supports READ and WRITE access levels.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { groupApi, SchemaGroup, OrganizationAssignment, AccessLevel } from '@/services/api/groupApi';
import { useOrganizationStore, Organization } from '@/stores/organization/organization-store';
import * as errorUtils from '@/utils/errors';

interface AssignOrganizationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: SchemaGroup | null;
  onSuccess: () => void;
}

function isAccessLevel(value: string): value is AccessLevel {
  return value === 'READ' || value === 'WRITE';
}

export const AssignOrganizationsDialog: React.FC<AssignOrganizationsDialogProps> = ({
  open,
  onOpenChange,
  group,
  onSuccess,
}) => {
  // State
  const [assignedOrgs, setAssignedOrgs] = useState<OrganizationAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add org form
  const [newOrgId, setNewOrgId] = useState('');
  const [newAccessLevel, setNewAccessLevel] = useState<AccessLevel>('READ');
  const [isAdding, setIsAdding] = useState(false);

  // Organization store for dropdown
  const {
    organizations,
    allOrganizations,
    fetchAllOrganizations,
    isLoading: orgsLoading,
    platformAdminEntitlement,
  } = useOrganizationStore();

  const fetchAssignedOrgs = useCallback(async () => {
    if (!group) return;

    setIsLoading(true);
    setError(null);

    try {
      const orgs = await groupApi.getAssignedOrganizations(group.id);
      setAssignedOrgs(orgs);
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to load assigned organizations');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [group]);

  // Fetch data when dialog opens
  useEffect(() => {
    if (open && group && platformAdminEntitlement) {
      fetchAssignedOrgs();
      fetchAllOrganizations();
    }
  }, [open, group, platformAdminEntitlement, fetchAllOrganizations, fetchAssignedOrgs]);

  const availableOrgs = useMemo(() => {
    const assignedIds = new Set(assignedOrgs.map(a => a.organizationId));
    const orgSource = platformAdminEntitlement ? allOrganizations : organizations;
    return orgSource.filter(org => !assignedIds.has(org._id));
  }, [organizations, allOrganizations, assignedOrgs, platformAdminEntitlement]);

  // Handle adding an organization
  const handleAddOrg = useCallback(async () => {
    if (!platformAdminEntitlement) {
      setError('Platform admin access is required.');
      return;
    }
    if (!group || !newOrgId) return;

    if (assignedOrgs.some(a => a.organizationId === newOrgId)) {
      setError('This organization is already assigned to this group.');
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      await groupApi.assignOrganization(group.id, newOrgId, newAccessLevel);
      await fetchAssignedOrgs();
      setNewOrgId('');
      setNewAccessLevel('READ');
      onSuccess();
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to assign organization');
      setError(errorMessage);
    } finally {
      setIsAdding(false);
    }
  }, [group, newOrgId, newAccessLevel, assignedOrgs, fetchAssignedOrgs, onSuccess, platformAdminEntitlement]);

  // Handle updating access level
  const handleUpdateAccess = useCallback(
    async (orgId: string, accessLevel: AccessLevel) => {
      if (!platformAdminEntitlement) {
        setError('Platform admin access is required.');
        return;
      }
      if (!group) return;

      try {
        await groupApi.updateOrganizationAccess(group.id, orgId, accessLevel);
        await fetchAssignedOrgs();
        onSuccess();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to update access level');
        setError(errorMessage);
      }
    },
    [group, fetchAssignedOrgs, onSuccess, platformAdminEntitlement]
  );

  // Handle removing an organization
  const handleRemoveOrg = useCallback(
    async (orgId: string) => {
      if (!platformAdminEntitlement) {
        setError('Platform admin access is required.');
        return;
      }
      if (!group) return;

      if (!window.confirm('Are you sure you want to remove this organization from the group?')) {
        return;
      }

      try {
        await groupApi.removeOrganization(group.id, orgId);
        await fetchAssignedOrgs();
        onSuccess();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to remove organization');
        setError(errorMessage);
      }
    },
    [group, fetchAssignedOrgs, onSuccess, platformAdminEntitlement]
  );

  // Format org ID for display (show first and last 4 chars)
  const formatOrgId = (orgId: string): string => {
    if (orgId.length <= 10) return orgId;
    return `${orgId.slice(0, 6)}...${orgId.slice(-4)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Assign Organizations
            {group && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                to {group.name}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {!platformAdminEntitlement ? (
          <div className="py-4 text-sm text-muted-foreground">
            Platform admin access is required.
          </div>
        ) : (
        <div className="space-y-6 py-4">
          {/* Add Organization Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Add Organization</h3>

            <div className="flex gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="org-select">Organization</Label>
                <Select value={newOrgId} onValueChange={setNewOrgId}>
                  <SelectTrigger id="org-select">
                    <SelectValue placeholder={orgsLoading ? "Loading organizations..." : "Select an organization..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableOrgs.length === 0 ? (
                      <div className="p-2 text-sm text-muted-foreground text-center">
                        {orgsLoading ? 'Loading...' : 'No organizations available'}
                      </div>
                    ) : (
                      availableOrgs.map((org) => (
                        <SelectItem key={org._id} value={org._id}>
                          {org.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28 space-y-2">
                <Label htmlFor="access-select">Access</Label>
                <Select
                  value={newAccessLevel}
                  onValueChange={(value) => {
                    if (isAccessLevel(value)) {
                      setNewAccessLevel(value);
                    }
                  }}
                >
                  <SelectTrigger id="access-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="READ">Read</SelectItem>
                    <SelectItem value="WRITE">Write</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="self-end">
                <Button
                  onClick={handleAddOrg}
                  disabled={!newOrgId || isAdding}
                >
                  {isAdding ? 'Adding...' : 'Add'}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              <strong>Read:</strong> Can query data. <strong>Write:</strong> Can create, update, and delete data.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Assigned Organizations Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">
              Assigned Organizations ({assignedOrgs.length})
            </h3>

            {isLoading ? (
              <div className="text-center py-4 text-muted-foreground">
                Loading organizations...
              </div>
            ) : assignedOrgs.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                No organizations assigned to this group yet.
              </div>
            ) : (
              <div className="space-y-2">
                {assignedOrgs.map((assignment) => {
                  const org = (platformAdminEntitlement ? allOrganizations : organizations)
                    .find(o => o._id === assignment.organizationId);
                  return (
                    <div
                      key={assignment.organizationId}
                      className="flex items-center justify-between p-3 border border-border rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        {/* Org Icon */}
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          <svg
                            className="w-4 h-4 text-muted-foreground"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            />
                          </svg>
                        </div>

                        {/* Org Info */}
                        <div>
                          <div className="text-sm font-medium">{org?.name || formatOrgId(assignment.organizationId)}</div>
                          <button
                            className="text-xs text-muted-foreground hover:text-foreground font-mono"
                            onClick={() => navigator.clipboard.writeText(assignment.organizationId)}
                            title="Copy organization ID"
                          >
                            {formatOrgId(assignment.organizationId)} • Click to copy
                          </button>
                        </div>
                      </div>

                      {/* Access Level + Remove */}
                      <div className="flex items-center gap-2">
                        <Select
                          value={assignment.accessLevel}
                          onValueChange={(value) => {
                            if (isAccessLevel(value)) {
                              handleUpdateAccess(assignment.organizationId, value);
                            }
                          }}
                        >
                          <SelectTrigger className="w-24 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="READ">Read</SelectItem>
                            <SelectItem value="WRITE">Write</SelectItem>
                          </SelectContent>
                        </Select>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveOrg(assignment.organizationId)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 h-8 w-8 p-0"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
            <strong>Note:</strong> Assigned organizations can access the schemas in this group.
            The owning organization (group creator) always has full access regardless of this list.
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssignOrganizationsDialog;
